import { Component, effect, inject, input } from '@angular/core';

import { EChartsOption, SeriesOption, YAXisComponentOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { RegisterComponent } from '../../components/component-lookup';
import { VisualBaseComponent } from '../visual-base.component';
import { DateRange, Peer, StateService } from '../../services/state.service';
import { VisualService } from '../../services/visual.service';

@RegisterComponent('Sparkline')
@Component({
  selector: 'app-sparkline',
  standalone: true,
  imports: [NgxEchartsDirective],
  templateUrl: './sparkline.component.html',
  styleUrl: './sparkline.component.scss',
  providers: [provideEcharts()]
})
export class SparklineComponent extends VisualBaseComponent {
  metricId = input.required<string>();
  displayType = input.required<string>();
  percentile = input<boolean>(false);

  readonly institution = inject(StateService).institution;
  readonly peer = inject(StateService).peer;
  readonly peerName = inject(StateService).peerName;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly dateRange = inject(StateService).dateRange;

  series: SeriesOption[] = !this.percentile()
    ? [{
      type: 'line',
      symbol: 'none'
    },
    {
      type: 'line',
      symbol: 'none'
    }]
    : [{
      type: 'line',
      symbol: 'none'
    }]

  override initOptions = { height: 150 }
  override baseOptions: EChartsOption = {
    grid: {
      left: 0,
      right: 0,
      top: 10,
      bottom: 20,
      containLabel: false
    },
    title: {
      show: false
    },
    xAxis: {
      type: 'category',
      show: false,
    },
    yAxis: {
      type: 'value',
      show: false
    },
    series: this.series
  };
  override tooltipOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      position: this.tooltipPosition,
      textStyle: {
        fontSize: 11
      },
      valueFormatter: value => value != null ? VisualService.getValueDisplay(Number(value), this.displayType()) : '--'
    }
  };
  override printOptions: EChartsOption = {
    animation: false,
    series: [
      {
        type: 'line',
        symbol: 'none'
      },
      {
        type: 'line',
        symbol: 'none',
        symbolSize: 7,
        lineStyle: {
          type: [20, 5],
          dashOffset: 5,
        }
      }
    ]
  };

  constructor() {
    super();

    effect(() => {
      const peer = this.peer();
      const dateRange = this.dateRange();
      const metricId = this.metricId();
      const group = this.filteredGroup();
      if (this.chartInit() && peer && dateRange && metricId && group) {
        this.updateVisual(peer, dateRange, metricId);
      }
    });
  }

  public updateVisual(peer: Peer, dateRange: DateRange, metricId: string) {
    this.metricService.getMetricData(this.institution().iid, metricId, peer)?.subscribe(data => {
      const dataSeries = this.visualService.getMetricSeriesPeer(this.displayType(), data, dateRange);

      const series: SeriesOption[] = !this.percentile()
        ? [{
          name: this.institution()?.name,
          data: dataSeries.institutionSeries,
          color: this.colors[0],
          symbol: 'line',
          symbolSize: 0,
        },
        {
          name: this.peerName(),
          data: dataSeries.peerSeries,
          color: this.colors[1],
          symbol: 'line',
          symbolSize: 0,
        }]
        : [{
          name: this.institution()?.name,
          data: dataSeries.institutionSeries,
          color: this.colors[0],
          symbol: 'line',
          symbolSize: 0,
          markLine: {
            silent: true,
            symbol: 'none',
            data: [
              {
                yAxis: 50,
                lineStyle: {
                  color: '#ccc',
                  type: 'solid',
                  width: 1
                }
              }
            ]
          }
        }];

      const yAxis: YAXisComponentOption = !this.percentile()
        ? {
          type: 'value',
          show: false
        }
        : {
          type: 'value',
          show: false,
          min: 0,
          max: 100
        }

      if (dataSeries.institutionSeries.filter(v => v != null).length == 0) {
        this.echartsInstance?.clear();
        this.chartOptions = this.noDataOptions;
      }
      else {
        this.updateOptions = {
          xAxis: {
            data: dataSeries.dateSeries
          },
          yAxis: yAxis,
          series: series
        };
      }
      this.echartsInstance?.hideLoading();
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public tooltipPosition(canvasMousePos: any, params: any, tooltipDom: any, rect: any, sizes: any) {
    const margin = 10;
    const overflowMargin = 5;

    const canvasRect = tooltipDom.parentElement.getElementsByTagName('canvas')[0].getBoundingClientRect();

    const mouseX = canvasMousePos[0] + canvasRect.x;
    const mouseY = canvasMousePos[1] + canvasRect.y;

    const tooltipWidth = sizes.contentSize[0];
    const tooltipHeight = sizes.contentSize[1];

    let xPos = mouseX + margin;
    let yPos = mouseY - margin - tooltipHeight;

    if (xPos + tooltipWidth >= document.documentElement.clientWidth) {
      xPos = mouseX - margin - tooltipWidth;
      if (xPos <= 0)
        xPos = overflowMargin;
    }

    if (yPos <= 0) {
      yPos = mouseY + margin;
      if (yPos + tooltipHeight >= document.documentElement.clientHeight)
        yPos = overflowMargin;
    }

    return [xPos - canvasRect.x, yPos - canvasRect.y]
  }
}
